import { z } from "zod";
import { LINT_CODES } from "./codes.js";

/**
 * Нормализованная модель сезона — то, что лежит в season_revisions.content и что
 * читают движок, озвучка, плеер и мессенджер. Файл креатора (YAML по SEASON_FORMAT.md)
 * превращается в неё парсером; обратно в файл ничего не превращается.
 *
 * Инварианты, которые держит форма, а не проверки:
 * - развилок нет: серии, сцены и реплики — только массивы, без ссылок «куда дальше»;
 * - длительности — секунды, а не строки «6h»;
 * - у каждой реплики стабильный key: по нему озвучка переиспользуется между ревизиями.
 */

export const FORMAT_VERSION = 1;

/** Метка имени подписчика в тексте реплики. */
export const NAME_TOKEN = "{name}";

export const HeroFormatSchema = z.enum(["chat", "audio"]);
export type HeroFormat = z.infer<typeof HeroFormatSchema>;

export const WeekdaySchema = z.enum(["mon", "tue", "wed", "thu", "fri", "sat", "sun"]);
export type Weekday = z.infer<typeof WeekdaySchema>;

/**
 * Ритм выхода. `every` уже нормализован: "2 days" из файла здесь "2days".
 * `at` — локальное время подписчика, "HH:MM". `weekday` — для недельных выпусков;
 * для "3nights" дни фиксированы: пн, ср, пт.
 */
export const ReleaseRuleSchema = z.object({
  every: z.enum(["day", "2days", "3days", "week", "3nights"]),
  at: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  weekday: WeekdaySchema.optional(),
});
export type ReleaseRule = z.infer<typeof ReleaseRuleSchema>;

const key = z.string().min(8);

export const LineSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("text"), key, text: z.string().min(1) }),
  z.object({ kind: z.literal("voice"), key, text: z.string().min(1) }),
  z.object({ kind: z.literal("photo"), key, asset: z.string().min(1) }),
  z.object({ kind: z.literal("video"), key, asset: z.string().min(1) }),
  z.object({ kind: z.literal("sound"), key, asset: z.string().min(1) }),
  z.object({ kind: z.literal("pause"), key, seconds: z.number().positive() }),
  /** Карточка другого героя: ссылка на его поддомен, не переход сюжета. */
  z.object({ kind: z.literal("hero"), key, slug: z.string().min(1) }),
]);
export type Line = z.infer<typeof LineSchema>;
export type LineKind = Line["kind"];

export const TransitionSchema = z.enum(["pause", "reply", "goal"]);
export type Transition = z.infer<typeof TransitionSchema>;

export const STORY_LIMITS = {
  textChars: 2_000,
  itemChars: 500,
  themes: 12,
  continuity: 50,
  beats: 30,
} as const;

const StoryTextSchema = z.string().trim().min(1).max(STORY_LIMITS.textChars);
const StoryItemSchema = z.string().trim().min(1).max(STORY_LIMITS.itemChars);

/** Dramaturgical plan for the whole season. Optional for revisions created before v3 story authoring. */
export const SeasonStorySchema = z.object({
  logline: StoryTextSchema,
  premise: StoryTextSchema,
  themes: z.array(StoryItemSchema).min(1).max(STORY_LIMITS.themes),
  audiencePromise: StoryTextSchema,
  arc: z.object({
    setup: StoryTextSchema,
    escalation: StoryTextSchema,
    climax: StoryTextSchema,
    resolution: StoryTextSchema,
  }),
  continuity: z.array(StoryItemSchema).max(STORY_LIMITS.continuity),
});
export type SeasonStory = z.infer<typeof SeasonStorySchema>;

/** Dramaturgical purpose and progression of one episode. */
export const EpisodeStorySchema = z.object({
  synopsis: StoryTextSchema,
  dramaticFunction: StoryTextSchema,
  turningPoint: StoryTextSchema,
  cliffhanger: StoryTextSchema,
});
export type EpisodeStory = z.infer<typeof EpisodeStorySchema>;

/** Screenplay context for one scene, separate from its delivered dialogue lines. */
export const SceneStorySchema = z.object({
  heading: StoryTextSchema,
  synopsis: StoryTextSchema,
  objective: StoryTextSchema,
  conflict: StoryTextSchema,
  beats: z.array(StoryItemSchema).min(1).max(STORY_LIMITS.beats),
  reveal: StoryTextSchema.optional(),
  stateAfter: StoryTextSchema,
});
export type SceneStory = z.infer<typeof SceneStorySchema>;

export const SceneSchema = z.object({
  ordinal: z.number().int().positive(),
  story: SceneStorySchema.optional(),
  /** Только chat: чего человек должен узнать к концу сцены. */
  goal: z.string().min(1).optional(),
  /** Пауза после предыдущей сцены; у первой сцены серии не учитывается. */
  pauseSeconds: z.number().int().nonnegative(),
  /** Только chat; в audio всегда "pause" и не используется. */
  transition: TransitionSchema,
  /** Только chat: через сколько напомнить, если подписчик молчит. */
  remindAfterSeconds: z.number().int().positive().optional(),
  lines: z.array(LineSchema),
});
export type Scene = z.infer<typeof SceneSchema>;

export const EpisodeSchema = z.object({
  ordinal: z.number().int().positive(),
  title: z.string().min(1),
  story: EpisodeStorySchema.optional(),
  /** Бесплатная серия (пилот). Считается от freeEpisodes, хранится для удобства чтения. */
  free: z.boolean(),
  /** Переопределение общего ритма сезона. */
  release: ReleaseRuleSchema.optional(),
  /** Только audio: целевая длина серии в минутах. */
  lengthMin: z.number().positive().optional(),
  /** Только audio: фон серии — имя файла в медиатеке героя. */
  background: z.string().min(1).optional(),
  scenes: z.array(SceneSchema).min(1),
});
export type Episode = z.infer<typeof EpisodeSchema>;

/** Только audio: выпуск без сюжета (ASMR, ужин, дождь), свой ритм. */
export const RubricSchema = z.object({
  key: z.string().min(1),
  title: z.string().min(1),
  release: ReleaseRuleSchema,
  background: z.string().min(1).optional(),
  lines: z.array(LineSchema).min(1),
});
export type Rubric = z.infer<typeof RubricSchema>;

/** Только audio: короткое голосовое с именем по расписанию. */
export const VoiceNoteSchema = z.object({
  key: z.string().min(1),
  release: ReleaseRuleSchema,
  lines: z.array(LineSchema).min(1),
});
export type VoiceNote = z.infer<typeof VoiceNoteSchema>;

export const SeasonContentSchema = z.object({
  formatVersion: z.literal(FORMAT_VERSION),
  season: z.number().int().positive(),
  title: z.string().min(1),
  story: SeasonStorySchema.optional(),
  format: HeroFormatSchema,
  /** BCP-47, например "en". */
  language: z.string().min(2),
  freeEpisodes: z.number().int().positive(),
  /** Ритм по умолчанию для серий без своего release. */
  release: ReleaseRuleSchema,
  episodes: z.array(EpisodeSchema).min(1),
  rubrics: z.array(RubricSchema),
  voiceNotes: z.array(VoiceNoteSchema),
});
export type SeasonContent = z.infer<typeof SeasonContentSchema>;

/**
 * Отчёт линтера. path — путь в файле: episodes[2].scenes[1].lines[4], индексы с нуля.
 * code — из словаря codes.ts: студия сопоставляет по нему подсказку и место в редакторе.
 */
export const LintIssueSchema = z.object({
  code: z.enum(LINT_CODES),
  path: z.string(),
  message: z.string().min(1),
});
export type LintIssue = z.infer<typeof LintIssueSchema>;

export const LintReportSchema = z.object({
  errors: z.array(LintIssueSchema),
  warnings: z.array(LintIssueSchema),
  stats: z.object({
    episodes: z.number().int().nonnegative(),
    scenes: z.number().int().nonnegative(),
    lines: z.number().int().nonnegative(),
    voiceChars: z.number().int().nonnegative(),
    /** Оценка длины каждой серии в секундах (audio); по индексу серии. */
    estimatedEpisodeSeconds: z.array(z.number().nonnegative()),
  }),
});
export type LintReport = z.infer<typeof LintReportSchema>;

/**
 * Что нужно линтеру сверх файла: формат героя, медиатека, известные герои.
 * Всё это приносит вызывающий код; сам линтер в базу не ходит.
 */
export interface LintContext {
  heroFormat: HeroFormat;
  /** Имена файлов в медиатеке героя. */
  assets: ReadonlySet<string>;
  /** Слаги героев, на которых можно ссылаться карточкой. */
  knownHeroSlugs: ReadonlySet<string>;
  /** Запрещённые слова и темы платформы; пусто — проверка пропускается. */
  bannedTerms?: readonly string[];
}
